'use client';

import React, { useState, useTransition, useOptimistic, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MotionDiv } from '@/components/motion';
import { Loader2 } from 'lucide-react';
import { submitContactForm } from '../actions';
import { useUser } from '@/hooks/useUser';

// Define character limits
const CHAR_LIMITS = {
  NAME: 50,
  EMAIL: 100,
  SUBJECT: 100,
  MESSAGE: 1000,
};

// Define contact form schema
const contactFormSchema = z.object({
  name: z.string().min(2, 'Nombre es requerido').max(CHAR_LIMITS.NAME, `El nombre debe tener máximo ${CHAR_LIMITS.NAME} caracteres`),
  email: z.string().email('Email inválido').max(CHAR_LIMITS.EMAIL, `El email debe tener máximo ${CHAR_LIMITS.EMAIL} caracteres`),
  subject: z.string().min(3, 'Asunto es requerido').max(CHAR_LIMITS.SUBJECT, `El asunto debe tener máximo ${CHAR_LIMITS.SUBJECT} caracteres`),
  message: z.string().min(1, 'Mensaje es requerido').max(CHAR_LIMITS.MESSAGE, `El mensaje debe tener máximo ${CHAR_LIMITS.MESSAGE} caracteres`),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export default function ContactForm() {
  const [isPending, startTransition] = useTransition();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [optimisticData, setOptimisticData] = useOptimistic<ContactFormData | null>(null);
  const { user, isLoading: isUserLoading } = useUser();
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
  });
  
  // Pre-fill form with user data if available
  useEffect(() => {
    if (user && !isUserLoading) {
      // Set name from user data
      if (user.firstName || user.lastName) {
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
        setValue('name', fullName);
      } else if (user.fullName) {
        setValue('name', user.fullName);
      }
      
      // Set email from user data
      if (user.email) {
        setValue('email', user.email);
      }
    }
  }, [user, isUserLoading, setValue]);

  const onSubmit = async (formData: FormData) => {
    const data = Object.fromEntries(formData.entries()) as unknown as ContactFormData;
    setOptimisticData(data);
    setSubmitError('');
    
    startTransition(async () => {
      const result = await submitContactForm(formData);
      
      if (result.success) {
        setSubmitSuccess(true);
        reset();
        setOptimisticData(null);
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(result.errors?.form || 'Hubo un error al enviar el mensaje.');
        setOptimisticData(null);
      }
    });
  };

  // Sanitize function to remove non-standard characters
  const sanitizeInput = (input: string, maxLength?: number): string => {
    if (!input) return '';
    
    // Remove any characters that aren't in the allowed set
    const sanitized = input.replace(/[^\w áéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ.,;:'"!?@#$%&*()_+\-=[\]{}|\\/<>]/g, '');
    
    // Apply length limit if provided
    if (maxLength && sanitized.length > maxLength) {
      return sanitized.substring(0, maxLength);
    }
    
    return sanitized;
  };

  // Handle input sanitization
  const handleInputChange = (field: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maxLength = field === 'name' ? CHAR_LIMITS.NAME 
                   : field === 'email' ? CHAR_LIMITS.EMAIL
                   : field === 'subject' ? CHAR_LIMITS.SUBJECT
                   : CHAR_LIMITS.MESSAGE;
    
    const sanitizedValue = sanitizeInput(e.target.value, maxLength);
    setValue(field as keyof ContactFormData, sanitizedValue);
    
    // Update the input value directly
    e.target.value = sanitizedValue;
  };
  
  return (
    <MotionDiv 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white p-8 rounded-lg shadow-lg"
    >
      <h2 className="text-2xl font-bold mb-6">Envíanos un mensaje</h2>
      
      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg">
          ¡Mensaje enviado con éxito! Nos pondremos en contacto con usted lo más pronto posible.
        </div>
      )}
      
      {submitError && (
        <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
          {submitError}
        </div>
      )}
      
      <form action={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nombre Completo
          </label>
          <input
            id="name"
            type="text"
            className={`w-full rounded-md border ${errors.name ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isUserLoading ? 'bg-gray-50' : ''}`}
            placeholder={isUserLoading ? "Cargando..." : "Su nombre"}
            maxLength={CHAR_LIMITS.NAME}
            autoComplete="name"
            {...register('name')}
            onChange={(e) => handleInputChange('name', e)}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Correo Electrónico
          </label>
          <input
            id="email"
            type="email"
            className={`w-full rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isUserLoading ? 'bg-gray-50' : ''}`}
            placeholder={isUserLoading ? "Cargando..." : "su@email.com"}
            maxLength={CHAR_LIMITS.EMAIL}
            autoComplete="email"
            {...register('email')}
            onChange={(e) => handleInputChange('email', e)}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Asunto
          </label>
          <input
            id="subject"
            type="text"
            className={`w-full rounded-md border ${errors.subject ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Asunto de su mensaje"
            maxLength={CHAR_LIMITS.SUBJECT}
            autoComplete="off"
            list="subject-options"
            {...register('subject')}
            onChange={(e) => handleInputChange('subject', e)}
          />
          <datalist id="subject-options">
            <option value="Consulta sobre productos" />
            <option value="Solicitud de cotización" />
            <option value="Información de envíos" />
            <option value="Soporte técnico" />
            <option value="Revendedores" />
            <option value="Otros" />
          </datalist>
          {errors.subject && (
            <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Mensaje
          </label>
          <textarea
            id="message"
            rows={4}
            className={`w-full rounded-md border ${errors.message ? 'border-red-500' : 'border-gray-300'} px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Escriba su mensaje aquí..."
            maxLength={CHAR_LIMITS.MESSAGE}
            {...register('message')}
            onChange={(e) => handleInputChange('message', e)}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
          )}
        </div>
        
        <button
          type="submit"
          disabled={isPending}
          className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md transition duration-300 flex items-center justify-center ${isPending ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isPending ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              Enviando...
            </>
          ) : (
            'Enviar Mensaje'
          )}
        </button>
      </form>
    </MotionDiv>
  );
} 